import type { Treaty } from '@elysiajs/eden'
import { queryOptions } from '@tanstack/vue-query'
import { api, useQuery } from '@/plugins/api2'
import { queryClient, queryPresets } from '@/plugins/tanstack'

/**
 * The response of `GET /api/v1/sys/config`, derived from the server through eden — never redeclared
 * here, so a change to the server's response schema shows up as a type error at the call site.
 * `Treaty.Data` accepts the endpoint function itself and unwraps its awaited return type.
 */
export type SysConfigsData = Treaty.Data<typeof api.sys.config.get>

/**
 * A single server configuration row. The response never carries anything beyond key/value/description
 * (plus createdAt), and `password`-like secrets are not part of this resource.
 */
export type SysConfig = SysConfigsData['entries'][number]

/**
 * The body for `PATCH /api/v1/sys/config/{key}`. `value` is required by the server schema; a cleared
 * input is sent as an empty string.
 */
export interface UpsertSysConfigInput {
  value: string
  description?: string | null
  /**
   * Only meaningful for keys an administrator adds: the server ships the type of its own keys along
   * with their description and ignores any attempt to change it.
   */
  type?: SysConfig['type']
}

export const sysConfigQueryKeys = {
  sysConfig: {
    key: ['sys-config'] as const,
  },
}

export function getSysConfigsQueryKey() {
  return [...sysConfigQueryKeys.sysConfig.key]
}

/**
 * Plain `queryOptions` — use `useSysConfigsQuery` for declarative consumption inside components, or
 * `queryClient.fetchQuery` for imperative calls. Both share the same key and cache entry.
 */
export function getSysConfigsQueryOpts() {
  return queryOptions({
    queryKey: getSysConfigsQueryKey(),
    queryFn: async ({ signal }) => {
      const { data, error } = await api.sys.config.get({ fetch: { signal } })
      if (error) {
        throw error.value
      }
      return data
    },
    ...queryPresets.shortLived,
  })
}

export function useSysConfigsQuery() {
  return useQuery(getSysConfigsQueryKey(), () => {
    return api.sys.config.get()
  })
}

/**
 * The keys the server cannot work without, as flagged by the rows themselves. Derived from the same
 * query the table uses, so both views agree and only one request is made.
 */
export function useSysConfigStatus() {
  const { data } = useSysConfigsQuery()

  const missingRequiredKeys = computed(() =>
    (data.value?.entries ?? []).filter(entry => entry.required && !entry.value).map(entry => entry.key),
  )

  return { missingRequiredKeys }
}

/**
 * Invalidates the sys-config cache entry the next time something observes it again.
 */
export function refreshSysConfigRelatedQueries() {
  return queryClient.invalidateQueries({ queryKey: sysConfigQueryKeys.sysConfig.key })
}

/**
 * Create-or-update a generic server configuration key (the endpoint is an upsert). Refreshes the
 * related queries so the table reflects the change immediately.
 */
export async function upsertSysConfig(key: string, input: UpsertSysConfigInput) {
  const { data, error } = await api.sys.config({ key }).patch(input)
  if (error) {
    throw error.value
  }
  await refreshSysConfigRelatedQueries()
  return data
}

/**
 * Soft-delete a generic server configuration key.
 */
export async function deleteSysConfig(key: string) {
  const { error } = await api.sys.config({ key }).delete()
  if (error) {
    throw error.value
  }
  await refreshSysConfigRelatedQueries()
}
